import { useRef } from "react";
import type { ReactElement, ReactNode } from "react";
import { Box, ButtonGroup, Container, Flex, Spacer } from "@chakra-ui/react";
import { BreadcrumbJsonLd, NextSeo, OrganizationJsonLd } from "next-seo";
import CopyLinkButton from "components/common/copy-link-button";
import CreateCitationButton from "components/profile/citation/create-citation-button";
import DownloadPdfButton from "components/profile/download-pdf-modal/download-pdf-button";
import EditProfileButton from "components/profile/edit-profile-button";
import {
  ProfileHeader,
  ProfileHeaderName,
  ProfileHeaderStat,
  ProfileHeaderStats,
} from "components/profile/profile-header";
import ProfileLogo from "components/profile/profile-logo";
import RequestUpdateButton from "components/profile/update-modals/request-update-button";
import { RequestUpdateProvider } from "components/profile/update-modals/request-update-modal";
import SuggestUpdateButton from "components/profile/update-modals/suggest-update-button";
import useIsStuck from "hooks/use-is-stuck";
import usePermission from "hooks/use-permission";
import useUser from "hooks/use-user";
import { getMetaDescription } from "utils/string";
import type { CompanyInitialDataResponse } from "types/api/company";
import ActiveTab from "./active-tab";
import AddToWatchlistButton from "./add-to-watchlist-button";
import {
  CompanyClassificationStat,
  CompanyStatusStat,
} from "./company-header-stats";
import LogoOrFallback from "./logo-or-fallback";
import TabHover from "./tab-hover";

interface CompanyPageLayoutProps {
  children: ReactNode;
  company: CompanyInitialDataResponse;
  slug: string;
  isInternational: boolean;
}

const CompanyPageLayout = ({
  children,
  company,
  slug = "",
  isInternational,
}: CompanyPageLayoutProps): ReactElement => {
  const hq = `${
    company.headquartersCity ? `${company.headquartersCity},` : ""
  } ${company.headquartersState || company.headquartersCountryName}`;

  const isAdmin = usePermission("admin");

  const user = useUser();
  const isPublic = !user?.isLoggedIn || false;
  const isFree = user?.access?.group === "free";

  const containerRef = useRef();

  const [navRef, isStuck] = useIsStuck({
    root: containerRef.current,
  });

  const hasFinancialsPermission = usePermission("companyFinancial");
  const hasFundingPermission = usePermission("companyFunding");
  const hasDealsPermission = usePermission("companyDeal");
  const hasSignalsPermission = usePermission("companySignal");
  const hasPwmPermission = usePermission("companyPrivateWealthManagement");
  const hasCompanyContactsPermission = usePermission("companyContacts");

  const pageTitle = `${company.name} Company Profile: Financials, Valuation, and Growth`;
  const canonicalUrl = `https://system.privco.com/company/${slug}`;

  const ogImageParams = new URLSearchParams({
    type: "company",
    name: company.name,
    imageUrl: company.logo,
  });
  const ogImageUrl = `/api/og?${ogImageParams.toString()}`;

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={company.summary}
        canonical={canonicalUrl}
        openGraph={{
          title: pageTitle,
          description: getMetaDescription(company.summary),
          url: canonicalUrl,
          images: [
            {
              url: ogImageUrl,
              alt: company.name,
            },
          ],
        }}
      />
      <OrganizationJsonLd
        type="Corporation"
        id={canonicalUrl}
        url={canonicalUrl}
        logo={company.logo}
        name={company.name}
        description={company.summary}
        address={{
          ...(company.headquartersCity && {
            addressLocality: company.headquartersCity,
          }),
          ...(company.headquartersState && {
            addressRegion: company.headquartersState,
          }),
          ...(company.headquartersCountryName && {
            addressCountry: company.headquartersCountryName,
          }),
        }}
        tickerSymbol={
          company.companyPublicTickers?.length
            ? company.companyPublicTickers[0].split(":").join(" ")
            : undefined
        }
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: company.name,
            item: canonicalUrl,
          },
        ]}
      />

      <RequestUpdateProvider
        resource="company"
        identifier={{ name: company.name, id: company.id, slug }}
      >
        <Box
          ref={containerRef}
          bg="white"
          as={Flex}
          flexDirection="column"
          h="100%"
          overflowY="auto"
        >
          <ProfileHeader>
            <ProfileLogo
              name={company.name}
              src={company.logo}
              showEditLink={isAdmin}
              id={company.id}
            />

            <ProfileHeaderName name={company.name}>
              {isPublic ? (
                <SuggestUpdateButton
                  identifier={{ name: company.name, id: company.id, slug }}
                  type="company"
                />
              ) : (
                <RequestUpdateButton isInternational={isInternational} />
              )}

              <CreateCitationButton
                name={company.name}
                slug={slug}
                type="company"
              />

              <CopyLinkButton
                label="Copy Profile Link"
                variant="solid"
                data-cy="copy-company-profile-link"
              />

              <EditProfileButton
                profileId={company.id}
                type="company"
                data-cy="edit-company-button"
              />
            </ProfileHeaderName>

            <ProfileHeaderStats>
              <ProfileHeaderStat label="Headquarters">{hq}</ProfileHeaderStat>

              <CompanyClassificationStat
                classification={company.classification}
                stockTickers={company.companyPublicTickers}
              />

              {(isPublic || isFree) ? null : <CompanyStatusStat tags={company.companyTags} />}
            </ProfileHeaderStats>
          </ProfileHeader>

          {!isPublic && (
            <Box
              ref={navRef}
              position="sticky"
              top="-1px"
              pt="1px"
              bg="white"
              borderBottomWidth={1}
              zIndex={3}
            >
              <Container maxW="8xl">
                <Flex
                  aria-label="Company Profile Navigation"
                  as="nav"
                  align="center"
                  overflowX="auto"
                  overflowY="visible"
                  mx={-4}
                  px={4}
                  py={1}
                  my={-1}
                >
                  <Flex
                    display={isStuck ? "flex" : "none"}
                    px={2}
                    h="40px"
                    alignSelf="center"
                    align="center"
                    mr={2}
                    bg="slate.100"
                    rounded="md"
                    flexShrink={0}
                  >
                    <LogoOrFallback logo={company.logo} name={company.name} />
                    <Box fontWeight="medium">{company.name}</Box>
                  </Flex>
                  <TabHover>
                    <ActiveTab slug={`/company/${slug}`}>Overview</ActiveTab>
                    {hasFinancialsPermission && (
                      <ActiveTab slug={`/company/${slug}/financials`}>
                        Financials
                      </ActiveTab>
                    )}
                    {hasFundingPermission && (
                      <ActiveTab slug={`/company/${slug}/funding`}>
                        Funding
                      </ActiveTab>
                    )}
                    {hasDealsPermission && (
                      <ActiveTab slug={`/company/${slug}/deals`}>
                        Deals
                      </ActiveTab>
                    )}
                    {hasSignalsPermission && (
                      <ActiveTab slug={`/company/${slug}/signals`}>
                        Signals
                      </ActiveTab>
                    )}
                    {hasPwmPermission && (
                      <ActiveTab slug={`/company/${slug}/wealth`}>
                        Wealth
                      </ActiveTab>
                    )}
                    {hasCompanyContactsPermission && (
                      <ActiveTab slug={`/company/details/${slug}/contacts`}>
                        Contacts
                      </ActiveTab>
                    )}
                  </TabHover>

                  <Spacer />

                  <ButtonGroup
                    ml={3}
                    colorScheme="blue"
                    variant="outline"
                    size="sm"
                  >
                    <AddToWatchlistButton id={company.id} />

                    <DownloadPdfButton
                      id={company.id}
                      slug={slug}
                      type="company"
                    />
                  </ButtonGroup>
                </Flex>
              </Container>
            </Box>
          )}

          <Box bg="slate.100" flex={1}>
            {children}
          </Box>
        </Box>
      </RequestUpdateProvider>
    </>
  );
};

export default CompanyPageLayout;